import { useCallback, useState } from 'react'

import {
  detachPaymentMethod,
  setDefaultPaymentMethod,
} from '../../utilities/api'
import { Delete } from '../ModalContent/Delete'
import { AddPaymentMethodModal } from './AddPaymentMethodModal'

export function useCustomerPaymentMethods({
  stripeCustomerId,
  connectAccountId,
  preventLastPaymentMethod = false,
  deleteConfirmText = 'This card will no longer be saved for future charges.',
  openModal,
  closeModal,
  onReload,
}) {
  const [processingPaymentMethodId, setProcessingPaymentMethodId] = useState('')
  const [paymentMethodsError, setPaymentMethodsError] = useState('')

  const handleSavePaymentMethodSuccess = useCallback(
    async ({ paymentMethodId, replacementPaymentMethodId }) => {
      if (!stripeCustomerId || !paymentMethodId) {
        await onReload?.()
        return
      }

      await setDefaultPaymentMethod({
        stripeCustomerId,
        paymentMethodId,
        connectAccountId,
      })

      if (replacementPaymentMethodId) {
        await detachPaymentMethod({
          paymentMethodId: replacementPaymentMethodId,
          stripeCustomerId,
          connectAccountId,
          preventLastPaymentMethod,
        })
      }

      await onReload?.()
    },
    [connectAccountId, onReload, preventLastPaymentMethod, stripeCustomerId]
  )

  const openReplacePaymentMethodModal = useCallback(
    (replacementPaymentMethodId = '') => {
      if (!stripeCustomerId) return
      openModal(
        AddPaymentMethodModal,
        {
          stripeCustomerId,
          connectAccountId,
          replacementPaymentMethodId,
          onSuccess: handleSavePaymentMethodSuccess,
        },
        { className: 'modal--lg' }
      )
    },
    [
      connectAccountId,
      handleSavePaymentMethodSuccess,
      openModal,
      stripeCustomerId,
    ]
  )

  const handleSetDefaultPaymentMethod = useCallback(
    async (paymentMethodId) => {
      if (!stripeCustomerId || !paymentMethodId) return
      setProcessingPaymentMethodId(paymentMethodId)
      setPaymentMethodsError('')
      try {
        await setDefaultPaymentMethod({
          stripeCustomerId,
          paymentMethodId,
          connectAccountId,
        })
        await onReload?.()
      } catch (err) {
        setPaymentMethodsError(
          err?.response?.data?.error?.message ||
            err?.message ||
            'Unable to set default payment method'
        )
      } finally {
        setProcessingPaymentMethodId('')
      }
    },
    [connectAccountId, onReload, stripeCustomerId]
  )

  const handleDetachPaymentMethod = useCallback(
    async (paymentMethodId) => {
      if (!stripeCustomerId || !paymentMethodId) {
        return Promise.reject(new Error('Missing payment method context'))
      }
      setProcessingPaymentMethodId(paymentMethodId)
      setPaymentMethodsError('')
      try {
        const response = await detachPaymentMethod({
          paymentMethodId,
          stripeCustomerId,
          connectAccountId,
          preventLastPaymentMethod,
        })
        await onReload?.()
        return response
      } catch (err) {
        setPaymentMethodsError(
          err?.response?.data?.error?.message ||
            err?.message ||
            'Unable to remove payment method'
        )
        throw err
      } finally {
        setProcessingPaymentMethodId('')
      }
    },
    [connectAccountId, onReload, preventLastPaymentMethod, stripeCustomerId]
  )

  const openDeletePaymentMethodModal = useCallback(
    (paymentMethodId) => {
      openModal(
        Delete,
        {
          entity: 'saved card',
          text: deleteConfirmText,
          id: paymentMethodId,
          handleDelete: handleDetachPaymentMethod,
          onClose: closeModal,
        },
        { className: 'modal--lg' },
        { replace: false }
      )
    },
    [closeModal, deleteConfirmText, handleDetachPaymentMethod, openModal]
  )

  return {
    processingPaymentMethodId,
    paymentMethodsError,
    setPaymentMethodsError,
    handleSetDefaultPaymentMethod,
    openReplacePaymentMethodModal,
    openDeletePaymentMethodModal,
  }
}
